import { PlainInput2 } from 'components/PlainInput/PlainInput2'
import { useEffect, useMemo, useState } from 'react'
import { Checkbox, Input2, Modal } from 'stories/components'
import { formatCurrency } from 'utils'

const adjustFieldLabels: Record<string, string> = {
  principal: 'Principal',
  interest: 'Interest',
  defaultInterest: 'Default Interest',
  mortgage: 'Mortgage Insurance',
  escrows: 'To Impound (Excl. MI)',
  buydown: 'Buydown Funds',
  lateFee: 'Late Fees',
}

export const adjustFieldKeys = Object.keys(adjustFieldLabels)

type AdjustFieldKey = typeof adjustFieldKeys[number]

/**
 * Where the amount this payment did not collect goes.
 *
 * `nextPayment` / `payoff` both carry it as a receivable the borrower still owes. `waive` forgives
 * it: the amount is still booked, so the loan keeps the record that it was charged, and then waived
 * on the spot - the same trail a charge waived from the Charges screen leaves.
 */
export type DeferOption = 'nextPayment' | 'payoff' | 'waive'

/**
 * The categories a shortfall can be waived in. Only these two are carried as a balance of their own
 * (Unpaid Interest / Unpaid Late Charges), so only these two are something there is a waiver to
 * record - the rest of the allocation is simply money this payment did not apply.
 */
export const waivableAdjustKeys = ['interest', 'lateFee']

interface LoanPaymentAdjustModalProps {
  /**
   * Everything the payment has to spend: the borrower's cash plus every bucket drawn as a source of
   * funds. Not the Amount Paid field, which is the cash alone - the shortfall this dialog exists to
   * reconcile is measured against the whole of it, and measuring against the cash reported one over
   * money the sources already covered.
   */
  appliedFunds: number
  payCharges: number
  values: Record<string, number>
  onClose: () => void
  onConfirm: (
    adjustedValues: Record<string, number>,
    diferedValues: Record<string, { amount: number; deferOption: DeferOption; reason?: string }>,
  ) => void
}

export const LoanPaymentAdjustModal = ({
  appliedFunds,
  payCharges,
  values,
  onClose,
  onConfirm,
}: LoanPaymentAdjustModalProps) => {
  const [adjustedValues, setAdjustedValues] = useState<Record<string, number>>({ ...values })
  const [deferSelections, setDeferSelections] = useState<Record<string, DeferOption>>({})
  /** Why the waived amounts are being given up. Optional - the amount, date and user are the trail. */
  const [waiveReason, setWaiveReason] = useState('')

  useEffect(() => {
    setAdjustedValues({ ...values })
    setDeferSelections({})
    setWaiveReason('')
  }, [values])

  const totalCredited = useMemo(() => {
    return adjustFieldKeys.reduce((sum, key) => sum + (adjustedValues[key] || 0), 0) + payCharges
  }, [adjustedValues])

  const difference = useMemo(() => {
    return totalCredited > appliedFunds ? Number((totalCredited - appliedFunds).toFixed(2)) : 0
  }, [totalCredited, appliedFunds])

  const visibleFields = useMemo(() => {
    return adjustFieldKeys.filter((key) => values[key] != 0)
  }, [values])

  const totalDefferAmount = useMemo(() => {
    return visibleFields.reduce((sum, key) => sum + (values[key] - (adjustedValues[key] || 0)), 0)
  }, [visibleFields, values, adjustedValues])

  const deferredFields = useMemo(() => {
    return visibleFields.filter((key) => values[key] - adjustedValues[key] != 0)
  }, [visibleFields, values, adjustedValues])

  const handleValueChange = (key: AdjustFieldKey, newValue: string) => {
    const numValue = parseFloat(newValue) || 0
    setAdjustedValues((prev) => ({
      ...prev,
      [key]: numValue,
    }))
  }

  const handleAdjust = (key: AdjustFieldKey) => {
    const currentFieldValue = adjustedValues[key]
    const adjustedFieldValue = Math.max(0, currentFieldValue - difference)
    setAdjustedValues((prev) => ({
      ...prev,
      [key]: adjustedFieldValue,
    }))
  }

  const handleDeferSelection = (key: string, option: DeferOption) => {
    setDeferSelections((prev) => ({
      ...prev,
      [key]: option,
    }))
  }

  const handleOk = () => {
    const diferedValues: Record<string, { amount: number; deferOption: DeferOption; reason?: string }> = {}
    deferredFields.forEach((key) => {
      diferedValues[key] = {
        amount: values[key] - adjustedValues[key],
        deferOption: deferSelections[key],
        ...(deferSelections[key] === 'waive' ? { reason: waiveReason.trim() } : {}),
      }
    })
    onConfirm(adjustedValues, diferedValues)
    return true
  }

  /** True once anything on this payment is being forgiven rather than carried. */
  const anyWaived = useMemo(
    () => deferredFields.some((key) => deferSelections[key] === 'waive'),
    [deferredFields, deferSelections],
  )

  const allDeferredSelected = useMemo(() => {
    return deferredFields.every((key) => deferSelections[key])
  }, [deferredFields, deferSelections])

  const isOkDisabled = difference !== 0 || (deferredFields.length > 0 && !allDeferredSelected)

  return (
    <Modal
      isOpen
      title="Adjust Payment Credits"
      titleOkay="Okay"
      onOk={handleOk}
      onClose={onClose}
      disabled={isOkDisabled}
    >
      <div className="w-196">
        {/* Summary Section */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Funds Applied:</span>
              <span className="font-semibold">${formatCurrency(appliedFunds)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium text-gray-600">Total Credited:</span>
              <span className={`font-semibold ${totalCredited > appliedFunds ? 'text-red-600' : 'text-green-600'}`}>
                ${formatCurrency(totalCredited)}
              </span>
            </div>
          </div>
          {difference > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between">
                <span className="font-medium text-red-600">Difference (Over-credited):</span>
                <span className="font-semibold text-red-600">${formatCurrency(difference)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Adjustable Values Table */}
        {visibleFields.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Original</th>
                  <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Adjusted Value</th>
                  <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">
                    Deffered Amount{' '}
                    {totalDefferAmount !== 0 && (
                      <span className="text-red-600">(${formatCurrency(totalDefferAmount)})</span>
                    )}
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleFields.map((key) => (
                  <tr key={key} className="border-t border-gray-200">
                    <td className="px-4 py-2 text-sm text-gray-700">{adjustFieldLabels[key]}</td>
                    <td className="px-4 py-2 text-sm text-gray-500 text-right">${formatCurrency(values[key])}</td>
                    <td className="px-4 py-2">
                      <PlainInput2
                        value={String(adjustedValues[key])}
                        content={`$${formatCurrency(adjustedValues[key])}`}
                        origin={String(adjustedValues[key])}
                        onChange={(newValue: string) => handleValueChange(key, newValue)}
                        type="number"
                        className="text-right text-sm"
                      />
                    </td>
                    <td className="px-4 py-2 text-center text-sm text-gray-600">
                      ${formatCurrency(values[key] - adjustedValues[key])}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleAdjust(key)}
                        disabled={difference === 0 || adjustedValues[key] < difference}
                        className={`btn-link ${
                          difference === 0 || adjustedValues[key] < difference
                            ? '!text-gray-400 !cursor-not-allowed'
                            : ''
                        }`}
                      >
                        Adjust
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info message */}
        {difference > 0 && (
          <p className="mt-4 text-sm text-gray-500">
            Adjust the values above so that the Total Credited equals the Amount Paid. The Okay button will be enabled
            when the difference is zero.
          </p>
        )}

        {/* Deferral Options Table */}
        {difference === 0 && deferredFields.length > 0 && (
          <div className="mt-6 border rounded-lg overflow-hidden">
            <div className="bg-blue-50 px-4 py-2 border-b">
              <span className="text-sm font-semibold text-blue-700">Select Deferral Option for Each Category</span>
            </div>
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Deffered Amount</th>
                  <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">
                    Defer until next payment
                  </th>
                  <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Defer until payoff</th>
                  {/* Forgive it instead of carrying it. Offered only on interest and late fees,
                      which are the two the loan carries a balance of. */}
                  <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Waive</th>
                </tr>
              </thead>
              <tbody>
                {deferredFields.map((key) => (
                  <tr key={key} className="border-t border-gray-200">
                    <td className="px-4 py-2 text-sm text-gray-700">{adjustFieldLabels[key]}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 text-right">
                      ${formatCurrency(values[key] - adjustedValues[key])}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <Checkbox
                        id={`defer-${key}-nextPayment`}
                        title=""
                        value={deferSelections[key] === 'nextPayment'}
                        onChange={() => handleDeferSelection(key, 'nextPayment')}
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <Checkbox
                        id={`defer-${key}-payoff`}
                        title=""
                        value={deferSelections[key] === 'payoff'}
                        onChange={() => handleDeferSelection(key, 'payoff')}
                      />
                    </td>
                    <td className="px-4 py-2 text-center">
                      {waivableAdjustKeys.includes(key) ? (
                        <Checkbox
                          id={`defer-${key}-waive`}
                          title=""
                          value={deferSelections[key] === 'waive'}
                          onChange={() => handleDeferSelection(key, 'waive')}
                        />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {anyWaived && (
              <div className="px-4 py-3 border-t bg-white">
                <p className="mb-2 text-[13px] text-gray-500">
                  A waived amount is still booked against the loan, then waived on the spot, so the Charges screen
                  keeps the record that it was charged and forgiven. Nothing further is billed, collected or quoted
                  on it.
                </p>
                <Input2
                  title="Why is it being waived? (optional)"
                  value={waiveReason}
                  onChange={(value: string) => setWaiveReason(value)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
